import type { FormInputComponent } from '../types'
import type { PreviewRoleType, Report, ReportSubmission } from '../types/preview'

interface RatingOption {
	value: string
	label: string
}

interface SelectOption {
	value: string
	label: string
}

/**
 * Get mock submitter labels based on who the viewer role would see reports from
 */
function getMockSubmitterLabels(viewerRole: PreviewRoleType): ReportSubmission[] {
	switch (viewerRole) {
		case 'participant':
			// Participants see reports from their manager
			return [{ label: 'Direct Manager', value: '', calculatedRole: 'primary_manager' }]
		case 'primary_manager':
		case 'secondary_manager':
			// Managers see reports from participant and raters
			return [
				{ label: 'Self Assessment', value: '', calculatedRole: 'participant' },
				{ label: 'Peer Reviewer 1', value: '', calculatedRole: 'rater' },
				{ label: 'Peer Reviewer 2', value: '', calculatedRole: 'rater' },
				{ label: 'Direct Report 1', value: '', calculatedRole: 'rater' },
			]
		default:
			// Raters typically don't see reports, but if they do
			return [
				{ label: 'Anonymous Response 1', value: '', calculatedRole: 'rater' },
				{ label: 'Anonymous Response 2', value: '', calculatedRole: 'rater' },
			]
	}
}

/**
 * Generate mock reports for a rating component
 */
function generateMockRatingReports(component: FormInputComponent, viewerRole: PreviewRoleType): Report[] {
	const custom = component.custom as { options?: RatingOption[] }
	const options = custom.options || [
		{ value: '1', label: '1' },
		{ value: '2', label: '2' },
		{ value: '3', label: '3' },
		{ value: '4', label: '4' },
		{ value: '5', label: '5' },
	]

	const submitters = getMockSubmitterLabels(viewerRole)
	const submissions: ReportSubmission[] = submitters.map((submitter, index) => {
		// Generate semi-random but consistent values (3-5 range)
		const optionIndex = Math.min(options.length - 1, 2 + (index % 3))
		return {
			label: submitter.label,
			value: options[optionIndex]?.value || String(optionIndex + 1),
			calculatedRole: submitter.calculatedRole,
		}
	})

	// Calculate average from numeric values
	const numericValues = submissions.map((s) => Number.parseFloat(s.value)).filter((n) => !Number.isNaN(n))
	const average =
		numericValues.length > 0 ? (numericValues.reduce((a, b) => a + b, 0) / numericValues.length).toFixed(1) : undefined

	return [
		{
			label: 'Responses',
			submissions,
			average,
		},
	]
}

/**
 * Generate mock reports for a text/textarea component
 */
function generateMockTextReports(_component: FormInputComponent, viewerRole: PreviewRoleType): Report[] {
	const submitters = getMockSubmitterLabels(viewerRole)
	const sampleResponses = [
		'This employee demonstrates strong leadership skills and consistently meets deadlines.',
		'Great team player who collaborates effectively with colleagues across departments.',
		'Shows initiative in problem-solving and brings creative solutions to challenges.',
		'Excellent communication skills and maintains positive working relationships.',
	]

	const submissions: ReportSubmission[] = submitters.map((submitter, index) => ({
		label: submitter.label,
		value: sampleResponses[index % sampleResponses.length],
		calculatedRole: submitter.calculatedRole,
	}))

	return [
		{
			label: 'Responses',
			submissions,
		},
	]
}

/**
 * Generate mock reports for a number component
 */
function generateMockNumberReports(component: FormInputComponent, viewerRole: PreviewRoleType): Report[] {
	const custom = component.custom as { min?: number; max?: number }
	const min = custom.min ?? 0
	const max = custom.max ?? 100

	const submitters = getMockSubmitterLabels(viewerRole)
	const submissions: ReportSubmission[] = submitters.map((submitter, index) => {
		// Generate values in the upper portion of the range
		const range = max - min
		const value = min + range * 0.6 + index * range * 0.1
		return {
			label: submitter.label,
			value: String(Math.round(Math.min(value, max))),
			calculatedRole: submitter.calculatedRole,
		}
	})

	const numericValues = submissions.map((s) => Number.parseFloat(s.value))
	const average = (numericValues.reduce((a, b) => a + b, 0) / numericValues.length).toFixed(1)

	return [
		{
			label: 'Responses',
			submissions,
			average,
		},
	]
}

/**
 * Generate mock reports for a select component
 */
function generateMockSelectReports(component: FormInputComponent, viewerRole: PreviewRoleType): Report[] {
	const custom = component.custom as { options?: SelectOption[] }
	const options = custom.options || []

	const submitters = getMockSubmitterLabels(viewerRole)
	const submissions: ReportSubmission[] = submitters.map((submitter, index) => ({
		label: submitter.label,
		value: options[index % options.length]?.label || `Option ${index + 1}`,
		calculatedRole: submitter.calculatedRole,
	}))

	return [
		{
			label: 'Responses',
			submissions,
		},
	]
}

/**
 * Generate mock reports for a checkbox component
 */
function generateMockCheckboxReports(_component: FormInputComponent, viewerRole: PreviewRoleType): Report[] {
	const submitters = getMockSubmitterLabels(viewerRole)
	const submissions: ReportSubmission[] = submitters.map((submitter, index) => ({
		label: submitter.label,
		value: index % 2 === 0 ? 'Yes' : 'No',
		calculatedRole: submitter.calculatedRole,
	}))

	return [
		{
			label: 'Responses',
			submissions,
		},
	]
}

/**
 * Generate mock reports based on component type
 */
export function generateMockReports(component: FormInputComponent, viewerRole: PreviewRoleType): Report[] {
	switch (component.name) {
		case 'rating':
		case '9boxrating':
			return generateMockRatingReports(component, viewerRole)
		case 'text':
		case 'textarea':
			return generateMockTextReports(component, viewerRole)
		case 'number':
			return generateMockNumberReports(component, viewerRole)
		case 'select':
			return generateMockSelectReports(component, viewerRole)
		case 'checkbox':
			return generateMockCheckboxReports(component, viewerRole)
		default:
			return []
	}
}
